import { ProviderProgramTaxonomy } from '../providers/Providers'

export enum ProgramNames {
  SingleSessionCoaching = 'SingleSessionCoaching',
  BlendedCareTherapy = 'BlendedCareTherapy',
  Coaching = 'Coaching',
  CoachingForParents = 'CoachingForParents',
  MedicationManagement = 'MedicationManagement',
  AlcoholUseDisorderTherapy = 'AlcoholUseDisorderTherapy',
  ClinicalLeaveEvaluation = 'ClinicalLeaveEvaluation',
  TeensTherapy = 'TeensTherapy',
  DirectAccessCouplesTherapy = 'DirectAccessCouplesTherapy',
  DirectAccessIndividualTherapy = 'DirectAccessIndividualTherapy',
  DirectAccessFamilyTherapy = 'DirectAccessFamilyTherapy',
  DirectAccessIndividualAdolescentTherapy = 'DirectAccessIndividualAdolescentTherapy',
  DirectAccessIndividualChildTherapy = 'DirectAccessIndividualChildTherapy',
  // Non LT programs
  DIRECT_ACCESS_MEDS = 'DIRECT_ACCESS_MEDS',
  SELF_CARE_APPS = 'SELF_CARE_APPS',
  DIRECT_ACCESS_THERAPY = 'DIRECT_ACCESS_THERAPY',
  ICASTherapy = 'ICASTherapy',
}

export type LTProgramName = Exclude<
  ProgramNames,
  'DIRECT_ACCESS_MEDS' | 'SELF_CARE_APPS' | 'DIRECT_ACCESS_THERAPY' | 'ICASTherapy' | 'DirectAccess'
>

export interface Program {
  appointment_change_window_hours: number
  appt_follow_up_ai_prompts?: string[]
  appt_initial_ai_prompts?: string[]
  appt_initial_mins: number
  appt_recurring_mins: number
  assessment_sets?: string[]
  compatible_programs: CompatibleProgram[]
  create_date: string
  curricula: unknown
  default_grace_period_hours: number
  employment_week_work_hours?: {
    [key: string]: number
  }
  gad_threshold?: number
  id: string
  late_cancel_charge_initial: number
  late_cancel_charge_recurring: number
  name: string
  phq_threshold?: number
  program_taxonomy: ProviderProgramTaxonomy[]
  provider_modality: string
  session_type_initial: string
  session_type_recurring: string
  utilization_target_percent?: {
    [key: string]: number
  }
  utilization_target_plus_percent?: {
    [key: string]: number
  }
}

interface CompatibleProgram {
  compatible_program_id: string
  id: string
  program_id: string
}

const ProgramNameLabels = new Map<ProgramNames, string>([
  [ProgramNames.ICASTherapy, 'Therapy'],
  [ProgramNames.BlendedCareTherapy, 'Therapy'],
  [ProgramNames.TeensTherapy, 'Teen Therapy'],
  [ProgramNames.Coaching, 'Coaching'],
  [ProgramNames.CoachingForParents, 'Coaching for Parents'],
  [ProgramNames.SingleSessionCoaching, 'Guided self-care'],
  [ProgramNames.MedicationManagement, 'Medication Management'],
  [ProgramNames.AlcoholUseDisorderTherapy, 'Renew'],
  [ProgramNames.DIRECT_ACCESS_MEDS, 'Lyra community meds'],
  [ProgramNames.SELF_CARE_APPS, ' Guided self-care'],
  [ProgramNames.DIRECT_ACCESS_THERAPY, 'Lyra community therapy'],
  [ProgramNames.ClinicalLeaveEvaluation, 'Clinical Leave Evaluation'],
  [ProgramNames.DirectAccessCouplesTherapy, 'Couples Therapy'],
  [ProgramNames.DirectAccessIndividualTherapy, 'Therapy'],
  [ProgramNames.DirectAccessFamilyTherapy, 'Family Therapy'],
  [ProgramNames.DirectAccessIndividualAdolescentTherapy, 'Teen Therapy'],
  [ProgramNames.DirectAccessIndividualChildTherapy, 'Child Therapy'],
])

const ProgramNameAgeGroup = new Map<ProgramNames, string>([
  [ProgramNames.TeensTherapy, '13-17'],
  [ProgramNames.DirectAccessIndividualChildTherapy, '0-12'],
  [ProgramNames.DirectAccessIndividualAdolescentTherapy, '13-17'],
])

export const MinorPrograms = new Set<ProgramNames>([
  ProgramNames.TeensTherapy,
  ProgramNames.DirectAccessIndividualChildTherapy,
  ProgramNames.DirectAccessIndividualAdolescentTherapy,
])

export const TeenPrograms = new Set<ProgramNames>([
  ProgramNames.TeensTherapy,
  ProgramNames.DirectAccessIndividualAdolescentTherapy,
])

const DAClientListProgramLabels = new Map<ProgramNames, string>([
  [ProgramNames.DirectAccessCouplesTherapy, 'Couples Therapy'],
  [ProgramNames.DirectAccessIndividualTherapy, 'Adult Therapy'],
  [ProgramNames.DirectAccessFamilyTherapy, 'Family Therapy'],
  [ProgramNames.DirectAccessIndividualAdolescentTherapy, 'Teen Therapy'],
  [ProgramNames.DirectAccessIndividualChildTherapy, 'Child Therapy'],
])

const ProgramNameAcronyms = new Map<ProgramNames, string>([
  [ProgramNames.ClinicalLeaveEvaluation, 'CLE'],
  [ProgramNames.CoachingForParents, 'CFP'],
  [ProgramNames.SingleSessionCoaching, 'GSC'],
  [ProgramNames.DirectAccessCouplesTherapy, 'Couples'],
  [ProgramNames.DirectAccessIndividualTherapy, 'Individual'],
  [ProgramNames.DirectAccessFamilyTherapy, 'Family'],
  [ProgramNames.DirectAccessIndividualAdolescentTherapy, 'Individual'],
  [ProgramNames.DirectAccessIndividualChildTherapy, 'Individual'],
])

const ClientProfileProgramNameLabels = new Map<ProgramNames, string>([
  [ProgramNames.ICASTherapy, 'Therapy'],
  [ProgramNames.BlendedCareTherapy, 'Therapy'],
  [ProgramNames.Coaching, 'Coaching'],
  [ProgramNames.MedicationManagement, 'Meds Management'],
  [ProgramNames.SELF_CARE_APPS, ' Guided self-care coaching'],
  [ProgramNames.SingleSessionCoaching, 'Guided self-care coaching'],
  [ProgramNames.AlcoholUseDisorderTherapy, 'Renew'],
  [ProgramNames.ClinicalLeaveEvaluation, 'Short Term Disability Eval'],
  [ProgramNames.DIRECT_ACCESS_THERAPY, 'Lyra community therapy'],
  [ProgramNames.DIRECT_ACCESS_MEDS, 'Lyra community meds'],
  [ProgramNames.TeensTherapy, 'Teens'],
  [ProgramNames.CoachingForParents, 'Coaching for Parents'],
  [ProgramNames.DirectAccessCouplesTherapy, 'Couples Therapy'],
  [ProgramNames.DirectAccessIndividualTherapy, 'Therapy'],
  [ProgramNames.DirectAccessFamilyTherapy, 'Family Therapy'],
  [ProgramNames.DirectAccessIndividualAdolescentTherapy, 'Teen Therapy'],
  [ProgramNames.DirectAccessIndividualChildTherapy, 'Child Therapy'],
])

const ProgramIdNames = new Map<string, ProgramNames>([
  ['7ec82ca3-843f-4996-bdf9-49d8c7a35ed5', ProgramNames.Coaching],
  ['5ee0996e-c7cd-46d1-b009-462f6379cbd5', ProgramNames.CoachingForParents],
  ['c8cb4da4-8957-4cff-9d8e-1fa742f06771', ProgramNames.BlendedCareTherapy],
  ['b42a0c74-76c0-48c4-8a7e-a767418bd753', ProgramNames.TeensTherapy],
  ['deb574e4-6aa3-4b47-bd21-d8be3ba5d42b', ProgramNames.SingleSessionCoaching],
  ['edcea864-b6eb-4d16-8c4c-ce748c18025e', ProgramNames.AlcoholUseDisorderTherapy],
  ['1b2b8b47-4c0e-49ff-a65c-40e187360b70', ProgramNames.MedicationManagement],
  ['6f146c5b-ac3b-42e5-b2a2-aacb3035e6d8', ProgramNames.ClinicalLeaveEvaluation],
  ['690f8981-70c3-4714-b667-464da34cd1c2', ProgramNames.DirectAccessCouplesTherapy],
  ['affa3537-78a4-4abf-b330-b7abc1a5740b', ProgramNames.DirectAccessIndividualTherapy],
  ['9cc004d9-614c-4a95-8176-d1ac89e5628a', ProgramNames.DirectAccessFamilyTherapy],
  ['c7034a1f-f506-4349-afab-69e173052238', ProgramNames.DirectAccessIndividualAdolescentTherapy],
  ['d125cead-10a1-4d90-9f9f-457ca3490e91', ProgramNames.DirectAccessIndividualChildTherapy],
])

/**
 * Helper function to get the program name label from the ProgramNameLabels map
 */
export const getProgramNameLabel = (
  programName: ProgramNames,
  isClientProfileLabelTag = false,
  acronymVersion = false,
  isClientList = false,
) => {
  return isClientProfileLabelTag
    ? ClientProfileProgramNameLabels.get(programName) ?? ''
    : acronymVersion
    ? ProgramNameAcronyms.get(programName) ?? ProgramNameLabels.get(programName) ?? ''
    : DA_ENGAGE_PROGRAMS.has(programName) && isClientList
    ? DAClientListProgramLabels.get(programName) ?? ''
    : ProgramNameLabels.get(programName) ?? ''
}

/**
 * Helper function to get the age group from the ProgramNameAgeGroup map
 */
export const getProgramNameAgeGroup = (programName: ProgramNames) => {
  return ProgramNameAgeGroup.get(programName) ?? ''
}

/**
 * Helper function to get the program name from the ProgramId
 */
export const getProgramNameFromId = (programId?: string | null) => {
  return programId ? ProgramIdNames.get(programId) ?? undefined : undefined
}

/**
 * Helper function to get the programId from programName
 */
export const getProgramIdFromName = (programName: ProgramNames) => {
  for (const [key, value] of ProgramIdNames.entries()) {
    if (value === programName) {
      return key
    }
  }
  return undefined
}

export function isCoachingProgram(programName: ProgramNames): boolean {
  return [ProgramNames.Coaching, ProgramNames.SingleSessionCoaching, ProgramNames.CoachingForParents].includes(
    programName,
  )
}
export const DA_ENGAGE_PROGRAMS = new Set<ProgramNames>([
  ProgramNames.DirectAccessCouplesTherapy,
  ProgramNames.DirectAccessIndividualTherapy,
  ProgramNames.DirectAccessFamilyTherapy,
  ProgramNames.DirectAccessIndividualAdolescentTherapy,
  ProgramNames.DirectAccessIndividualChildTherapy,
])

export function isDAEngageProgram(programName: ProgramNames | string): boolean {
  return programName ? DA_ENGAGE_PROGRAMS.has(programName as ProgramNames) : false
}

export const DA_MINOR_PROGRAMS = new Set<ProgramNames>([
  ProgramNames.DirectAccessIndividualAdolescentTherapy,
  ProgramNames.DirectAccessIndividualChildTherapy,
])

export function isDAMinorProgram(programName: ProgramNames | string): boolean {
  return programName ? DA_MINOR_PROGRAMS.has(programName as ProgramNames) : false
}

export const getCareTypeFromProgram = (programName: string | undefined): string | undefined => {
  const careTypeMap: Record<ProgramNames, string> = {
    [ProgramNames.ICASTherapy]: 'therapy',
    [ProgramNames.BlendedCareTherapy]: 'therapy',
    [ProgramNames.TeensTherapy]: 'therapy',
    [ProgramNames.Coaching]: 'coaching',
    [ProgramNames.SingleSessionCoaching]: 'coaching',
    [ProgramNames.CoachingForParents]: 'coaching',
    [ProgramNames.MedicationManagement]: 'medications management',
    [ProgramNames.AlcoholUseDisorderTherapy]: 'renew',
    [ProgramNames.DIRECT_ACCESS_MEDS]: 'Lyra community medications',
    [ProgramNames.SELF_CARE_APPS]: 'guided self care',
    [ProgramNames.DIRECT_ACCESS_THERAPY]: 'Lyra community therapy',
    [ProgramNames.ClinicalLeaveEvaluation]: 'short term disability evaluation',
    [ProgramNames.DirectAccessCouplesTherapy]: 'couples therapy',
    [ProgramNames.DirectAccessIndividualTherapy]: 'therapy',
    [ProgramNames.DirectAccessFamilyTherapy]: 'family therapy',
    [ProgramNames.DirectAccessIndividualAdolescentTherapy]: 'adolescent therapy',
    [ProgramNames.DirectAccessIndividualChildTherapy]: 'child therapy',
  }

  return (programName && careTypeMap[programName]) || ''
}
